/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon } from '@components/atoms'
import Dropzone, { DropzoneState, Accept } from 'react-dropzone'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	onFileChange: (aFile: File) => void
	onFileDropEnd: () => void
	showOpenFileBrowser?: boolean
	onCloseFileBrowser?: () => void
	children?: JSX.Element
	accept?: Accept
}

/** 파일 드롭존 — 헤어라인 프레임(점선 아님), 아이콘 하나, 고스트 버튼. 색·라운드 없음 */
export const FlieDropzone = (props: PropsType) => {
	const dropzoneRef = React.createRef<any>()

	React.useEffect(() => {
		if (props.showOpenFileBrowser !== undefined && props.showOpenFileBrowser) {
			dropzoneRef.current.open()
		}
	}, [props.showOpenFileBrowser])

	const onDrop = React.useCallback((acceptedFiles: DropzoneState['acceptedFiles']) => {
		acceptedFiles.map((aFile: File) => {
			const reader = new FileReader()
			reader.readAsDataURL(aFile)

			reader.onload = () => {
				props.onFileChange(aFile)
			}
		})

		onCloseFilebrowser()
		props.onFileDropEnd()
	}, [])

	const onCloseFilebrowser = () => {
		if (props.onCloseFileBrowser) {
			props.onCloseFileBrowser()
		}
	}

	return (
		<View css={style}>
			<Dropzone
				ref={dropzoneRef}
				onDrop={onDrop}
				onFileDialogCancel={onCloseFilebrowser}
				accept={
					props.accept ?? {
						'image/jpeg': ['.jpeg', '.jpg'],
						'image/png': ['.png'],
						'image/bmp': ['.bmp'],
						'image/gif': ['.gif'],
						'video/mp4': ['.mp4'],
						'video/mpeg': ['.mpeg'],
						'video/webm': ['.webm'],
						'video/quicktime': ['.mov'],
						'video/x-msvideo': ['.avi'],
						'text/plain': ['.txt'],
						'text/csv': ['.csv'],
						'application/vnd.ms-excel': ['.xls', '.xlsx'],
						'application/msword': ['.doc', '.docx'],
						'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
						'application/pdf': ['.pdf'],
					}
				}
			>
				{({ getRootProps, getInputProps }) => (
					<View className="dropzoneView">
						<input {...getInputProps()} />
						{props.children !== undefined ? (
							props.children
						) : (
							<View className="dropzoneOuterView">
								<View {...getRootProps({ className: 'importView dropzone' })}>
									<Icon className="fileUploadIcon" name="file-upload-outline" />
									<p className="dropzoneText">
										{"'파일 불러오기' 버튼을 클릭 또는 여기로 파일을 끌어오세요."}
									</p>
									<button type="button" className="fileUploadButton">
										파일 불러오기
									</button>
								</View>
							</View>
						)}
					</View>
				)}
			</Dropzone>
		</View>
	)
}

const style = css({
	fontFamily: boFont.ui,

	'.dropzoneOuterView': {
		'.importView': {
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 12,
			padding: '40px 24px',
			border: `1px solid ${mono.line}`,
			background: mono.surface,
			borderRadius: 0,
			textAlign: 'center',
			cursor: 'pointer',
			transition: 'border-color 200ms ease-out',

			'&:hover': { borderColor: mono.ink },

			'.fileUploadIcon': {
				fontSize: 40,
				color: mono.faint,
			},

			'.dropzoneText': {
				margin: 0,
				fontSize: 13,
				fontWeight: 300,
				lineHeight: 1.6,
				color: mono.dim,
				maxWidth: 360,
			},

			'.fileUploadButton': {
				display: 'inline-flex',
				alignItems: 'center',
				height: 34,
				marginTop: 8,
				padding: '0 16px',
				fontFamily: boFont.ui,
				fontSize: 13,
				fontWeight: 500,
				color: mono.ink,
				background: 'transparent',
				border: `1px solid ${mono.ink}`,
				borderRadius: 2,
				cursor: 'pointer',
				transition: 'color 200ms ease-out, background-color 200ms ease-out',
				'&:hover': { background: mono.ink, color: '#FFFFFF' },
			},
		},
	},
})
